/**
 * useAnalytics — injects Google Analytics 4, Google Tag Manager, and Facebook Pixel
 * dynamically based on the restaurant's saved integration settings.
 *
 * Call once in MainLayout (admin) and in PublicMenuPage (customer-facing).
 * Scripts are injected only once per tenant/config runtime. PageView events fire on
 * every route change. A tenant or integration-ID change forces a clean reload so
 * analytics globals from one restaurant can never be reused by another.
 *
 * Returns { pixelReady } — a boolean that becomes true once fbq is injected.
 * Consumers (e.g. Purchase tracking after gateway redirect) must depend on pixelReady
 * to avoid a race condition where orderConfirmed is set before restaurant loads.
 */
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "@/utils/tracking";

const injected = { ga: false, gtm: false, fbq: false, runtimeKey: "" };

export function getAnalyticsRuntimeKey(restaurant) {
  const tenantId = restaurant?.id;
  if (!tenantId) return "";

  const integrations = restaurant?.integrations || {};
  return [
    tenantId,
    integrations.google_analytics_id || "",
    integrations.google_tag_manager_id || "",
    integrations.facebook_pixel_id || "",
  ].join("|");
}

export function shouldReloadAnalyticsRuntime(activeKey, nextKey) {
  return Boolean(activeKey && nextKey && activeKey !== nextKey);
}

export function useAnalytics(restaurant) {
  const tenantId = restaurant?.id;
  const gaId    = restaurant?.integrations?.google_analytics_id;
  const gtmId   = restaurant?.integrations?.google_tag_manager_id;
  const pixelId = restaurant?.integrations?.facebook_pixel_id;
  const runtimeKey = getAnalyticsRuntimeKey(restaurant);
  const runtimeMatches = !injected.runtimeKey || injected.runtimeKey === runtimeKey;
  const { pathname } = useLocation();
  const lastPageView = useRef("");

  // Analytics libraries keep global state. If the SPA changes tenant or any
  // integration ID, a hard reload is the only reliable way to discard the
  // previous tenant's GA/GTM/Pixel runtime before sending another event.
  useEffect(() => {
    if (!runtimeKey) return;
    if (shouldReloadAnalyticsRuntime(injected.runtimeKey, runtimeKey)) {
      window.location.reload();
      return;
    }
    injected.runtimeKey = runtimeKey;
  }, [runtimeKey]);

  // pixelReady becomes true once window.fbq stub is available.
  // Use as a dependency in Purchase tracking effects to handle the race where
  // orderConfirmed is set (from gateway redirect) before restaurant data loads.
  // Initialize to true if fbq was already injected in a prior render (module-level flag).
  const [pixelReady, setPixelReady] = useState(injected.fbq && runtimeMatches);

  if (typeof window !== "undefined" && !window._pixelDebugLogged) {
    window._pixelDebugLogged = true;
    console.log("[PIXEL_DEBUG_ANALYTICS] useAnalytics mounted | pixelId:", pixelId, "| injected.fbq:", injected.fbq);
  }

  // Google Analytics 4 — inject once
  useEffect(() => {
    if (!gaId || !runtimeMatches || injected.ga) return;
    injected.ga = true;

    const script1 = document.createElement("script");
    script1.async = true;
    script1.dataset.kaisoTenantId = tenantId || "";
    script1.dataset.kaisoIntegration = "ga4";
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script1);

    const script2 = document.createElement("script");
    script2.dataset.kaisoTenantId = tenantId || "";
    script2.dataset.kaisoIntegration = "ga4-config";
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gaId}');
    `;
    document.head.appendChild(script2);
  }, [gaId, runtimeMatches, tenantId]);

  // GA4 — PageView on route change
  useEffect(() => {
    if (!gaId || !runtimeMatches || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", { page_path: pathname });
  }, [gaId, pathname, runtimeMatches]);

  // Google Tag Manager — inject once
  useEffect(() => {
    if (!gtmId || !runtimeMatches || injected.gtm) return;
    injected.gtm = true;

    const script = document.createElement("script");
    script.dataset.kaisoTenantId = tenantId || "";
    script.dataset.kaisoIntegration = "gtm";
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);

    // GTM noscript iframe
    const noscript = document.createElement("noscript");
    noscript.dataset.kaisoTenantId = tenantId || "";
    noscript.dataset.kaisoIntegration = "gtm-noscript";
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.prepend(noscript);
  }, [gtmId, runtimeMatches, tenantId]);

  // Facebook Pixel — inject once + first PageView
  useEffect(() => {
    if (!pixelId || !runtimeMatches) return;
    if (injected.fbq) {
      // Already injected (e.g. prior render cycle) — signal readiness so deferred events fire
      console.log("[PIXEL_DEBUG_ANALYTICS] fbq already injected, signaling readiness");
      setPixelReady(true);
      return;
    }
    console.log("[PIXEL_DEBUG_ANALYTICS] Starting fbq injection | pixelId:", pixelId);
    injected.fbq = true;

    // Inline init script — creates the fbq stub synchronously before fbevents.js loads.
    // Events called before fbevents.js finishes loading are queued and processed on load.
    const initScript = document.createElement("script");
    initScript.dataset.kaisoTenantId = tenantId || "";
    initScript.dataset.kaisoIntegration = "meta-pixel";
    initScript.textContent = `
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){
      n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window,document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init','${pixelId}');
      console.log("[PIXEL_DEBUG_ANALYTICS] fbq stub initialized via inline script");
    `;
    document.head.appendChild(initScript);

    // noscript fallback — required for Facebook pixel verification
    const noscript = document.createElement("noscript");
    noscript.dataset.kaisoTenantId = tenantId || "";
    noscript.dataset.kaisoIntegration = "meta-pixel-noscript";
    const img = document.createElement("img");
    img.height = 1;
    img.width = 1;
    img.style.cssText = "display:none";
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);

    console.log("[PIXEL_DEBUG_ANALYTICS] Calling setPixelReady(true)");
    setPixelReady(true);
  }, [pixelId, runtimeMatches, tenantId]);

  // Facebook Pixel — PageView on subsequent route changes (with event_id for dedup)
  useEffect(() => {
    if (!runtimeMatches || !injected.fbq || typeof window.fbq !== "function") return;
    // Wait until all mount effects (including tenant tracking init) complete.
    let cancelled = false;
    queueMicrotask(() => {
      const key = `${runtimeKey}:${pathname}`;
      if (!cancelled && injected.runtimeKey === runtimeKey && lastPageView.current !== key) {
        lastPageView.current = key;
        trackPageView();
      }
    });
    return () => { cancelled = true; };
  }, [pathname, runtimeMatches, runtimeKey, pixelReady]);

  // Consumers must never observe readiness from a previous tenant while the
  // clean runtime reload is pending.
  return { pixelReady: pixelReady && runtimeMatches };
}

/** Track a custom GA4 event */
export function trackEvent(eventName, params = {}) {
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
  }
}

/** Track a custom Facebook Pixel event.
 *  options.eventID must match the CAPI event_id for server-side deduplication.
 */
export function trackPixelEvent(eventName, params = {}, options = {}) {
  if (typeof window.fbq === "function") {
    window.fbq("track", eventName, params, options);
  }
}
